/* eslint react/prop-types: 0 */
// In order for the translations to work we need to initialize the worldready as the first thing when the application is loaded.
// initiate WorldReady
import '../../jsV2/helpers/initializeWorldReady';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Router } from 'react-router';
import { autoRehydrate, persistStore } from 'redux-persist';
import { asyncSessionStorage } from 'redux-persist/storages';

import { applyMiddleware, compose, createStore } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { ContextProvider as WorldReadyContextProvider } from '@paypalcorp/worldready-react';
import axios from 'axios';
import worldReadyData from '../../jsV2/helpers/worldReadyData';

// Component, Route, and Lib
import routeDidUpdate from './actions/actions-router';
import { initAuthPrompt } from './actions/actions-auth-prompt';
import Page from '../lib/page';

import largeScreenRoutes from './routes/LargeScreenRoutes';
import rootReducer from './reducers/index';
import getHistory from '../lib/HistoryHelper';
import { getParsedPathName } from '../lib/location-helper';
import getOpenDisputes from './actions/action-dispute-transactions';
import { isTrackersEnabledInHome } from '../lib/covidHelper';
import { isNewContactUsExpEnabled, isNewHomeExpEnabled } from '../../jsV2/helpers/twHelper';
import LayoutRenderer from './containers/LayoutRenderer';
import { getCustomerJourneySessionKey, setCustomerJourneySessionKey } from '../lib/globalHelper';
import { trackPage } from '../lib/analytics-helper';
import { isCustomerJourneyAPIEnabled } from '../lib/omnichannelHelper';

const middlewares = [thunkMiddleware];

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line  global-require
  const defaultLogger = require('redux-logger');

  middlewares.push(defaultLogger.createLogger());
}

const isLoggedIn = Page.isLoggedIn();

const store = createStore(rootReducer, undefined, compose(autoRehydrate(), applyMiddleware(...middlewares)));

persistStore(store, {
  storage: asyncSessionStorage,
  whitelist: ['modal', 'qualtricsActiveIntercept', 'L3TopicList', 'searchResult'],
});

// Todo: below `basename` should be updated to `US`.
const history = getHistory();
const isHomePage = Page.isHomePage();

window.histTest = history;

const currentLocation = getParsedPathName(history.location);
history.push(currentLocation);

store.dispatch(routeDidUpdate(currentLocation));

history.listen((location) => {
  store.dispatch(routeDidUpdate(location));
});

if (isLoggedIn && isHomePage && !isNewHomeExpEnabled() && !isNewContactUsExpEnabled()) {
  // Initalize Auth prompt display logic for on-call users
  store.dispatch(initAuthPrompt(Page.isDisplayAuthPrompt()));
  // TODO: Remove this logic during covid -19 clean up
  // Adding tracker remote config check for Covid-19 Enable tracker
  if (isTrackersEnabledInHome()) {
    store.dispatch(getOpenDisputes());
  }
}

// checking whether new home design is enabled or not
const isNewHomePage = Page.isHomePage() && isNewHomeExpEnabled();

// if remote config is enabled and customer journey session variable is not available and new home design is not enabled
// make the customer journey API call
if (Page.isLoggedIn() && !getCustomerJourneySessionKey() && isCustomerJourneyAPIEnabled() && !isNewHomePage) {
  setCustomerJourneySessionKey(true); // setting in the session as customer journey is called
  let fptiPageId = Page.getFPTIPageId();
  // if icf topic page, changing the pagename,
  // as we need to fire different fpti's for icf topic page and icf article page
  if (Page.isIcfTopicsPage()) {
    fptiPageId = 'ICFTopicPage';
  }

  trackPage({ id: `customerJourney${fptiPageId}`, link: 'smarthelp-customer-journey-api-called' });
  axios
    .post('/smarthelp/customer-journey', {
      _csrf: Page.getCSRFtoken(),
    })
    .then(() => {
      trackPage({ id: `customerJourneySuccess${fptiPageId}`, link: 'smarthelp-customer-journey-api-success' });
    })
    .catch(() => {
      trackPage({ id: `customerJourneyFailure${fptiPageId}`, link: 'smarthelp-customer-journey-api-failure' });
    });
}

const routeConfig = largeScreenRoutes; // Using CSS to control left side display logic
Page.removeSession('showContactOptions');

Page.setCanonicalTag();

ReactDOM.render(
  <Provider store={store}>
    <WorldReadyContextProvider locale={worldReadyData.locality.locale}>
      <Router history={history}>
        <LayoutRenderer mainContainer={routeConfig.mainContainer} sideBar={routeConfig.sideBar} />
      </Router>
    </WorldReadyContextProvider>
  </Provider>,
  document.querySelector('.smarthelp-react-app'),
);
